import React from 'react';
import cn from 'classnames';
import { BranchesOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import {
	getDefaultDesignModeSelection,
	getDesignModeOverrideCondition,
	getExactDesignModeOverride,
	removeExactDesignModeOverride,
	resolveDesignModeValueWithSource,
	setDesignModeValue,
	type DesignModeGroup,
	type DesignModeSelection,
	type DesignModeValue,
} from 'common/design-tokens';
import css from './DesignModeValueField.scss';

type RenderState<T> = {
	value: T;
	setValue: (value: T) => void;
};

type Props<T> = {
	value: DesignModeValue<T>;
	selection: DesignModeSelection;
	groups: DesignModeGroup[];
	disabled?: boolean;
	className?: string;
	children: (state: RenderState<T>) => React.ReactNode;
	onChange: (value: DesignModeValue<T>) => void;
};

function modeNames(selection: DesignModeSelection, groups: DesignModeGroup[]) {
	return Object.entries(selection).flatMap(([groupId, modeId]) => {
		const mode = groups.find(group => group.id === groupId)?.modes.find(item => item.id === modeId);
		return mode ? [mode.name] : [];
	});
}

export default function DesignModeValueField<T>({
	value,
	selection,
	groups,
	disabled,
	className,
	children,
	onChange,
}: Props<T>) {
	const condition = getDesignModeOverrideCondition(selection, groups);
	const isBase = Object.keys(condition).length === 0;
	const exactOverride = getExactDesignModeOverride(value, selection, groups);
	const resolved = resolveDesignModeValueWithSource(value, {
		...getDefaultDesignModeSelection(groups),
		...selection,
	});
	const provenance = isBase ? 'base' : exactOverride ? 'override' : 'inherited';
	const inheritedSelection = resolved.condition
		? resolved.condition
		: Object.fromEntries(
				Object.keys(condition).map(groupId => [groupId, getDefaultDesignModeSelection(groups)[groupId]])
			);
	const inheritedFrom = modeNames(inheritedSelection, groups).join(' + ') || 'Base';

	// Base describes ModeValue storage provenance. Story appearance controls runtime mode activation separately.
	const status =
		provenance === 'base' ? (
			<Tooltip title="Base value. Other modes inherit it until you edit them.">
				<span className={css.badge}>Base</span>
			</Tooltip>
		) : provenance === 'override' ? (
			<>
				<span className={css.badge}>Override</span>
				<Button
					className={css.reset}
					type="text"
					size="small"
					icon={<UndoOutlined />}
					disabled={disabled}
					onClick={() => onChange(removeExactDesignModeOverride(value, selection, groups))}
				>
					Reset override
				</Button>
			</>
		) : (
			<Tooltip title="Editing this value creates an override for the current mode selection.">
				<span className={css.badge}>
					<BranchesOutlined aria-hidden />
					Inherited from {inheritedFrom}
				</span>
			</Tooltip>
		);

	return (
		<div className={cn(css.field, className)} data-provenance={provenance}>
			<div className={css.control}>
				{children({
					value: isBase ? value.base : resolved.value,
					setValue: next => onChange(setDesignModeValue(value, selection, next, groups)),
				})}
			</div>
			<div className={css.status}>{status}</div>
		</div>
	);
}
